import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { CaretDown, MagicWand, Plus } from "@phosphor-icons/react";
import { createResumeSchema, ResumeDto } from "@reactive-resume/dto";
import { idSchema } from "@reactive-resume/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
} from "@reactive-resume/ui";
import { generateRandomName, kebabCase } from "@reactive-resume/utils";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { builderPath, uniqueResumeSlug } from "@/client/libs/resume-identity";
import {
  useCreateResume,
  useDeleteResume,
  useResumes,
  useUpdateResume,
} from "@/client/services/resume";
import { useImportResume } from "@/client/services/resume/import";
import { useUser } from "@/client/services/user";
import { useBillingStore } from "@/client/stores/billing";
import { useDialog } from "@/client/stores/dialog";

const formSchema = createResumeSchema.extend({ id: idSchema.optional() });

type FormValues = z.infer<typeof formSchema>;

const titleForUser = (name?: string | null) => {
  const trimmed = name?.trim();
  return trimmed ? `${trimmed}'s resume` : "";
};

export const ResumeDialog = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { resumes } = useResumes();
  const { isOpen, mode, payload, close } = useDialog<ResumeDto>("resume");
  const openPaywall = useBillingStore((s) => s.openPaywall);
  const [showSlug, setShowSlug] = useState(false);

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";

  const { createResume, loading: createLoading } = useCreateResume();
  const { updateResume, loading: updateLoading } = useUpdateResume();
  const { deleteResume, loading: deleteLoading } = useDeleteResume();
  const { importResume: duplicateResume, loading: duplicateLoading } = useImportResume();

  const loading = createLoading || updateLoading || deleteLoading || duplicateLoading;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: "", slug: "" },
  });

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen, payload]);

  useEffect(() => {
    if (!isCreate && !isDuplicate) return;
    const slug = kebabCase(form.watch("title"));
    if (!showSlug) form.setValue("slug", slug);
  }, [form.watch("title"), isCreate, isDuplicate, showSlug]);

  const onSubmit = async (values: FormValues) => {
    try {
      const firstRun = (resumes?.length ?? 0) === 0;

      if (isCreate) {
        const title = values.title.trim() || titleForUser(user?.name) || generateRandomName();
        const resume = await createResume({
          slug: showSlug && values.slug.trim() ? kebabCase(values.slug) : uniqueResumeSlug(title),
          title,
          visibility: "private",
        });
        close();
        navigate(builderPath(resume.id, firstRun));
        return;
      }

      if (isUpdate) {
        if (!payload.item?.id) return;

        await updateResume({
          ...payload.item,
          title: values.title,
          slug: values.slug,
        });
      }

      if (isDuplicate) {
        if (!payload.item?.id) return;

        const resume = await duplicateResume({
          title: values.title,
          slug: uniqueResumeSlug(values.title),
          data: payload.item.data,
        });
        close();
        navigate(builderPath(resume.id));
        return;
      }

      if (isDelete) {
        if (!payload.item?.id) return;

        await deleteResume({ id: payload.item?.id });
      }

      close();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 403) {
        close();
        const serverMessage = error.response?.data?.message as string | undefined;
        openPaywall(undefined, serverMessage ?? t`Subscribe to a plan to create resumes.`);
        return;
      }
      throw error;
    }
  };

  const onReset = () => {
    setShowSlug(false);
    if (isCreate) {
      const title = titleForUser(user?.name) || t`My resume`;
      form.reset({ title, slug: kebabCase(title) || "resume" });
    }
    if (isUpdate)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
    if (isDuplicate)
      form.reset({ title: `${payload.item?.title} (Copy)`, slug: `${payload.item?.slug}-copy` });
    if (isDelete)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
  };

  const onGenerateRandomName = () => {
    const name = generateRandomName();
    form.setValue("title", name);
    form.setValue("slug", kebabCase(name));
  };

  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent>
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>{t`Are you sure you want to delete your resume?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action cannot be undone. This will permanently delete your resume and cannot be recovered.`}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  {t`Delete`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  <Plus />
                  <h2>
                    {isCreate && t`Create a new resume`}
                    {isUpdate && t`Update an existing resume`}
                    {isDuplicate && t`Duplicate an existing resume`}
                  </h2>
                </div>
              </DialogTitle>
              <DialogDescription>
                {isCreate &&
                  t`Give it a name. We'll open the editor with your details already filled in.`}
                {isUpdate && t`Changed your mind about the name? Give it a new one.`}
                {isDuplicate && t`Give your old resume a new name.`}
              </DialogDescription>
            </DialogHeader>

            <FormField
              name="title"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Title`}</FormLabel>
                  <FormControl>
                    <div className="flex items-center justify-between gap-x-2">
                      <Input {...field} className="flex-1" />

                      {(isCreate || isDuplicate) && (
                        <Tooltip content={t`Generate a random title for your resume`}>
                          <Button
                            size="icon"
                            type="button"
                            variant="outline"
                            onClick={onGenerateRandomName}
                          >
                            <MagicWand />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </FormControl>
                  <FormDescription>
                    {t`Tip: You can name the resume referring to the position you are applying for.`}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {(isCreate || isDuplicate) && (
              <button
                type="button"
                onClick={() => setShowSlug((value) => !value)}
                className="text-muted-foreground inline-flex items-center gap-1 text-xs hover:text-foreground"
              >
                <CaretDown className={showSlug ? "rotate-180" : ""} size={12} />
                {t`Advanced`}
              </button>
            )}

            {(showSlug || isUpdate) && (
              <FormField
                name="slug"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`Slug`}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type="submit" disabled={loading}>
                {isCreate && t`Create and open`}
                {isUpdate && t`Save Changes`}
                {isDuplicate && t`Duplicate`}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
